import React, { useEffect, useRef } from 'react';
import { Code, CheckCircle, RefreshCw, Zap, ChevronDown, GitPullRequest, Globe } from 'lucide-react';
import { useScan } from '../contexts/ScanContext';
import { SeverityBadge, CategoryBadge, CONFIDENCE_CLASSES } from './SeverityBadge';
import { getSafeUrl, getSafeImageUrl } from '../utils/safeUrl';

// `isExpanded` / `onToggle` let a caller own the expand state (e.g. pentest
// results). When omitted, ScanContext's shared expand state is used.
// `readOnly` hides the fix actions, e.g. for a scan viewed without logging in.
const VulnerabilityItem = ({ vulnerability, isExpanded, onToggle, readOnly = false }) => {
  const { expandedVulnerability, toggleVulnerability, fixVulnerability, fixingVulns, fixProgressMap, fixLogsMap, scaAutofixEnabled } = useScan();
  const { id, severity: rawSeverity, category, scanner, title, description, status, file, line, code, fix, impact, remediation, prUrl, fixError, error, url, http_method, parameter, evidence } = vulnerability;
  const SEVERITY_LEVELS = new Set(['critical', 'high', 'medium', 'low']);
  const impactAsSeverity = typeof impact === 'string' && SEVERITY_LEVELS.has(impact.toLowerCase()) ? impact.toLowerCase() : undefined;
  const severity = rawSeverity ? rawSeverity.toLowerCase() : impactAsSeverity;
  const logsContainerRef = useRef(null);
  const expanded = isExpanded !== undefined ? isExpanded : expandedVulnerability === id;
  const handleToggle = onToggle || (() => toggleVulnerability(id));

  // Dynamic-analysis (pentest / DAST) findings are located by URL+method+
  // parameter+evidence, not file:line, and are report-only (no auto-fix / PR).
  const isDast = category === 'dynamic_analysis' || scanner === 'dast';

  // Derived per-vulnerability fix state
  const fixLogs = fixLogsMap[id] || [];
  const fixProgress = fixProgressMap[id] || 0;
  const isFixing = fixingVulns.includes(id);
  const canFix = category !== 'secret_detection';
  const isSCA = category === 'third_party_dependencies' || scanner === 'trivy';
  const canShowFix = !readOnly && !isDast && canFix && (!isSCA || scaAutofixEnabled);

  // Auto-scroll logs to bottom when new logs are added
  useEffect(() => {
    if (logsContainerRef.current && isFixing) {
      const container = logsContainerRef.current;
      container.scrollTop = container.scrollHeight;
    }
  }, [fixLogs, isFixing]);

  // Render fix logs if this vulnerability is being fixed
  const renderFixLogs = () => {
    if (!isFixing || fixLogs.length === 0) return null;

    // Show queued state with a simpler UI (check latest log to detect current state)
    const latestLog = fixLogs[fixLogs.length - 1];
    if (latestLog?.message?.startsWith('Queued')) {
      return (
        <div className="mt-4">
          <div className="flex items-center gap-2 px-3 py-2 bg-indigo-50 border border-indigo-100 rounded-md">
            <div className="w-4 h-4 border-2 border-indigo-200 border-t-indigo-500 rounded-full animate-spin" />
            <span className="text-sm text-indigo-700">{latestLog.message}</span>
          </div>
        </div>
      );
    }

    return (
      <div className="mt-4">
        <h5 className="text-gray-700 text-sm font-semibold mb-2">Auto-Fix Progress</h5>
        <div className="log-container" ref={logsContainerRef}>
          <div className="mb-2 w-full bg-gray-200 rounded-full h-2.5">
            <div
              className="bg-teal-600 h-2.5 rounded-full transition-all duration-300"
              style={{ width: `${fixProgress}%` }}
            ></div>
          </div>
          {fixLogs.map((log, index) => (
            <div key={index} className="flex items-start mb-1 text-sm">
              <span className="mr-2 text-lg">{log.icon}</span>
              <span className="text-gray-100">{log.message}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div
      className={`bg-white border border-gray-100 rounded-lg overflow-hidden shadow-sm ${
        status === 'fixed' ? 'border-l-4 border-l-green-500' :
        status === 'fix_failed' ? 'border-l-4 border-l-gray-400' :
        severity === 'critical' ? 'border-l-4 border-l-red-500' :
        severity === 'high' ? 'border-l-4 border-l-orange-500' :
        'border-l-4 border-l-yellow-500'
      }`}
    >
      {/* Vulnerability Header - Always visible */}
      <div
        className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={handleToggle}
      >
        {/* Stacks below `sm`: as a forced row the action column had no
            `flex-shrink-0`, so Auto-Fix was squeezed past the right edge and
            became untappable on a phone. */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
              <SeverityBadge severity={severity} status={status} size="sm" />
              <CategoryBadge category={category} scanner={scanner} size="sm" />
              <h4 className="text-gray-900 font-semibold break-words">{title}</h4>
            </div>
            <p className="text-gray-600 text-sm mb-2 line-clamp-2 break-words">{description}</p>
            {isDast ? (
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500 min-w-0">
                <Globe className="w-3 h-3 mr-1 flex-shrink-0" />
                {url && <span className="break-all font-mono">{url}</span>}
                {http_method && (
                  <span className="font-semibold uppercase text-gray-600">· {http_method}</span>
                )}
                {parameter && (
                  <span className="text-gray-500">· param: <span className="font-mono text-gray-700">{parameter}</span></span>
                )}
              </div>
            ) : (file || line != null) ? (
              <div className="flex items-center text-xs text-gray-500 min-w-0">
                <Code className="w-3 h-3 mr-1 flex-shrink-0" />
                <span className="break-all">{file}:{line}</span>
              </div>
            ) : null}
          </div>

          <div className="flex items-center justify-between gap-3 sm:justify-end sm:flex-shrink-0">
            {status === 'fixed' ? (
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center text-green-600 text-sm">
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Fixed
                </div>
                {getSafeUrl(prUrl) && (
                  <a
                    href={getSafeUrl(prUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-teal-600 text-sm hover:text-teal-700 transition-colors"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <GitPullRequest className="w-4 h-4 mr-1" />
                    View Pull Request
                  </a>
                )}
              </div>
            ) : status === 'fix_failed' || status === 'error' ? (
              <div className="flex flex-col items-start text-sm min-w-0 sm:items-end">
                <div className="flex items-center text-gray-500">
                  <RefreshCw className="w-4 h-4 mr-1 flex-shrink-0" />
                  Auto-fix failed
                </div>
                {(fixError || error) && (
                  <span
                    className="text-xs text-gray-400 mt-1 max-w-full truncate sm:max-w-xs"
                    title={fixError || error}
                  >
                    {fixError || error}
                  </span>
                )}
              </div>
            ) : isFixing ? (
              (() => { return (
              <div className="flex items-center text-teal-600 text-sm">
                <RefreshCw className="w-4 h-4 mr-1 flex-shrink-0 animate-spin" />
                Fixing... {fixProgress}%
              </div>
              ); })()
            ) : canShowFix ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  fixVulnerability(id);
                }}
                className="flex flex-shrink-0 items-center bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 py-2 rounded"
                disabled={isFixing}
              >
                <Zap className="w-3 h-3 mr-1 flex-shrink-0" />
                Auto-Fix
              </button>
            ) : null}
            <ChevronDown
              className={`w-5 h-5 flex-shrink-0 text-gray-400 transition-transform ${expanded ? 'transform rotate-180' : ''}`}
            />
          </div>
        </div>
      </div>

      {/* Expanded Vulnerability Details */}
      {expanded && (
        <div className="border-t border-gray-100 p-4 bg-gray-50">
          {/* Render fix logs if this vulnerability is being fixed */}
          {renderFixLogs()}

          {/* Evidence block for dynamic-analysis (DAST) findings.

              SECURITY: evidence is attacker-influenced text, stored verbatim and intentionally
              NOT HTML-escaped by the backend (escaping it would corrupt the payload bytes an
              analyst needs to see -- see _build_vulnerability_details in app_sast.py). It is safe
              here only because every field below is rendered as JSX text, which React escapes,
              and the screenshot goes through getSafeImageUrl. Keep it that way: never pass
              evidence to dangerouslySetInnerHTML, and never use it as an href/src unvalidated. */}
          {isDast && evidence && (
            <div className="mb-6 space-y-4">
              <h5 className="text-gray-700 text-sm font-semibold">Evidence</h5>
              {evidence.request && (
                <div>
                  <h6 className="text-gray-600 text-xs font-semibold mb-1">Request</h6>
                  <div className="bg-gray-900 rounded p-3 overflow-x-auto">
                    <pre className="text-xs text-gray-100 font-mono whitespace-pre-wrap break-all">{evidence.request}</pre>
                  </div>
                </div>
              )}
              {evidence.response && (
                <div>
                  <h6 className="text-gray-600 text-xs font-semibold mb-1">Response</h6>
                  <div className="bg-gray-900 rounded p-3 overflow-x-auto">
                    <pre className="text-xs text-gray-100 font-mono whitespace-pre-wrap break-all">{evidence.response}</pre>
                  </div>
                </div>
              )}
              {getSafeImageUrl(evidence.screenshot) && (
                <div>
                  <h6 className="text-gray-600 text-xs font-semibold mb-1">Screenshot</h6>
                  <img
                    src={getSafeImageUrl(evidence.screenshot)}
                    alt="Evidence screenshot"
                    className="max-w-full rounded border border-gray-200"
                  />
                </div>
              )}
              {(evidence.dom_snapshot || evidence.domSnapshot) && (
                <div>
                  <h6 className="text-gray-600 text-xs font-semibold mb-1">DOM Snapshot</h6>
                  <div className="bg-gray-900 rounded p-3 overflow-x-auto">
                    <pre className="text-xs text-gray-100 font-mono whitespace-pre-wrap break-all">{evidence.dom_snapshot || evidence.domSnapshot}</pre>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              {/* <h5 className="text-gray-700 text-sm font-semibold mb-2">Vulnerable Code</h5>
              <div className="bg-gray-900 rounded p-3 font-mono text-xs text-gray-100 overflow-x-auto mb-4">
                <pre>{code}</pre>
              </div> */}

              <h5 className="text-gray-700 text-sm font-semibold mb-2">Security Impact</h5>
              <p className="text-gray-600 text-sm mb-4">{impact}</p>
            </div>

           <div>
              {/* <h5 className="text-green-700 text-sm font-semibold mb-2">Recommended Fix</h5>
              <div className="bg-green-50 border border-green-200 rounded p-3 font-mono text-xs text-green-800 overflow-x-auto mb-4">
                <pre>{fix}</pre>
              </div>  */}

              <h5 className="text-gray-700 text-sm font-semibold mb-2">Remediation Advice</h5>
              {Array.isArray(remediation) ? (
                // Handle array format (Semgrep, Trivy)
                remediation.map((link, idx) => {
                  const url = typeof link === 'string' ? link : link.url;
                  const safeUrl = getSafeUrl(url);
                  if (!safeUrl) return null;
                  return (
                    <a
                      key={idx}
                      href={safeUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-teal-600 text-sm mb-2 underline hover:text-teal-700 block break-all"
                    >
                      {typeof link === 'string' ? link : (link.text || link.url)}
                    </a>
                  );
                })
              ) : typeof remediation === 'object' && remediation !== null ? (
                // Handle object/dict format (multi_agent_ai)
                <div className="text-gray-600 text-sm space-y-2">
                  {Object.entries(remediation).map(([key, value]) => (
                    <div key={key}>
                      <strong className="text-gray-700 capitalize">{key.replace(/_/g, ' ')}:</strong>
                      <p className="ml-2">{value}</p>
                    </div>
                  ))}
                </div>
              ) : getSafeUrl(remediation) ? (
                // Handle string format (fallback) - only render if URL is safe
                <a
                  href={getSafeUrl(remediation)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-teal-600 text-sm mb-4 underline hover:text-teal-700 break-all"
                >
                  {remediation}
                </a>
              ) : remediation ? (
                // Render as plain text if not a safe URL
                <p className="text-gray-600 text-sm mb-4">{remediation}</p>
              ) : null}
              {status !== 'fixed' && status !== 'fix_failed' && !isFixing && canShowFix && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    fixVulnerability(id);
                  }}
                  className="w-full flex items-center justify-center bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded"
                  disabled={isFixing}
                >
                  <Zap className="w-3 h-3 mr-2" />
                  Apply Auto-Fix
                </button>
              )}
            </div>
          </div>

          {/* Multi-Agent AI Specific Fields */}
          {scanner === 'multi_agent_ai' && (
            <div className="mt-6 border-t border-gray-200 pt-4">
              <h5 className="text-gray-700 text-sm font-semibold mb-3">Additional Analysis (Multi-Agent AI)</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {vulnerability.cwe_ids && vulnerability.cwe_ids.length > 0 && (
                  <div>
                    <h6 className="text-gray-600 text-xs font-semibold mb-1">CWE IDs</h6>
                    <p className="text-gray-700 text-sm">{vulnerability.cwe_ids.join(', ')}</p>
                  </div>
                )}
                {vulnerability.confidence && (
                  <div>
                    <h6 className="text-gray-600 text-xs font-semibold mb-1">Confidence Level</h6>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                      CONFIDENCE_CLASSES[vulnerability.confidence] || CONFIDENCE_CLASSES.low
                    }`}>
                      {vulnerability.confidence.toUpperCase()}
                    </span>
                  </div>
                )}
                {vulnerability.owasp_category && (
                  <div>
                    <h6 className="text-gray-600 text-xs font-semibold mb-1">OWASP Category</h6>
                    <p className="text-gray-700 text-sm">{vulnerability.owasp_category}</p>
                  </div>
                )}
                {vulnerability.risk_score !== undefined && (
                  <div>
                    <h6 className="text-gray-600 text-xs font-semibold mb-1">Risk Score</h6>
                    <p className="text-gray-700 text-sm">
                      <span className="font-bold text-lg">{vulnerability.risk_score}</span>/100
                    </p>
                  </div>
                )}
                {vulnerability.attack_vector && (
                  <div className="md:col-span-2">
                    <h6 className="text-gray-600 text-xs font-semibold mb-1">Attack Vector</h6>
                    <p className="text-gray-700 text-sm bg-white border border-gray-100 rounded p-2">{vulnerability.attack_vector}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VulnerabilityItem;
